import { Stage } from "../helpers/get-settings";
import {
  NEW_HIRE_VIDEO_URL,
  TAKE_BADGE_PHOTO_START_URL,
  USCIS_URL,
  PERSONAL_INFORMATION_COLLECTION_URL,
  BACKGROUND_CHECK_STATUS_ENDPOINT,
  DRUG_TEST_STATUS_ENDPOINT,
  BADGE_PHOTO_STATUS_ENDPOINT,
  NEW_HIRE_VIDEO_STATUS_ENDPOINT,
} from "./urls";
import React from "react";
import { Text } from "../components/typography";
import { Link } from "@amzn/stencil-react-components/link";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { I9Task } from "../components/i9-task";
import { StatusEnum } from "../hoc/with-status";
import { PICTask } from "../components/pic-task";
import { AppointmentDetailsTask } from "../components/appointment-details-task";

export interface PopoverProps {
  title: string;
  body?: string[];
  buttonText?: string;
}

interface TaskWithStatus {
  (status: StatusEnum, isExpanded?: boolean): React.ReactNode;
}

export interface Task {
  titleText: string;
  details: React.ReactNode | TaskWithStatus;
  buttonText?: string;
  href?: string | Record<Stage, string>;
  isLink?: boolean;
  analyticsOnAction?: string;
  analyticsOnExpand?: string;
  /**
   * The path where task status information can retrieved.
   */
  titleStatusPath?: string;
  subtasks?: Task[];
  errorStatusDetailMessage?: React.ReactNode;
  hideWhenError?: boolean;
  isFallback?: boolean;
  expandWhenNotStarted?: boolean;
  popover?: PopoverProps;
  disableWhenCompleted?: boolean;
  shouldOverrideStatus?: Record<string, StatusEnum>;
  hideInProd?: boolean;
  taskStatusName?: string;
  hideOnTaskStatusError?: boolean;
}

export const tasks: Array<Task> = [
  {
    titleText: "View pre-hire appointment details",
    details: function CompleteAppointmentDetailsTask(status: StatusEnum) {
      return <AppointmentDetailsTask status={status} />;
    },
    analyticsOnAction: "appointment details",
  },
  {
    titleText: "Watch the new hire video",
    details: "Learn more about working at Amazon.",
    buttonText: "Watch video",
    disableWhenCompleted: false,
    href: NEW_HIRE_VIDEO_URL,
    titleStatusPath: NEW_HIRE_VIDEO_STATUS_ENDPOINT,
  },
  {
    titleText: "Complete your I-9 Employment Authorization Form",
    details: function CompleteI9Task(status: StatusEnum, isExpanded: boolean) {
      return <I9Task isExpanded={isExpanded} />;
    },
    analyticsOnExpand: "complete employment form",
  },
  {
    titleText: "Pack I-9 documents for your appointment",
    details: (
      <Text>
        Please visit the link of{" "}
        <Link href={USCIS_URL} target="_blank">
          acceptable I-9 documents
        </Link>
        . You are required to bring these documents at your new hire
        appointment.
      </Text>
    ),
    analyticsOnExpand: "pack documents",
  },
  {
    titleText: "Take your badge photo",
    details:
      "Click on the button below to capture a selfie or upload your photo to be used for your Amazon badge.",
    analyticsOnExpand: "badge photo",
    disableWhenCompleted: true,
    buttonText: "Take a badge photo",
    href: TAKE_BADGE_PHOTO_START_URL,
    analyticsOnAction: "go to take badge photo",
    titleStatusPath: BADGE_PHOTO_STATUS_ENDPOINT,
    hideWhenError: true,
    expandWhenNotStarted: true,
    // popover: {
    //   title: "Want to save time during New Hire Event?",
    //   body: [
    //     "Try using this self-service option to submit your photo.",
    //     'To proceed, click on "Take a Badge Photo" Button',
    //   ],
    //   buttonText: "Ok",
    // },
    shouldOverrideStatus: {
      [StatusEnum.IN_PROGRESS]: StatusEnum.PENDING_REVIEW,
    },
  },
  {
    titleText: "Take your badge photo",
    details:
      "At your appointment we'll take a photo to use for your Amazon badge. You will be required to take off any sunglasses, hats, or other props that block or partially block the face or head. Religious headwear is acceptable.",
    analyticsOnExpand: "badge photo",
    titleStatusPath: BADGE_PHOTO_STATUS_ENDPOINT,
    isFallback: true,
  },
  {
    titleText: "Complete pre-employment screening",
    subtasks: [
      {
        titleText: "Background Check",
        titleStatusPath: BACKGROUND_CHECK_STATUS_ENDPOINT,
        details: <div></div>,
        errorStatusDetailMessage: (
          <MessageBanner type={MessageBannerType.Warning}>
            We were unable to process your Background Check. Please click{" "}
            <Link href="http://amazon.com/applicationhelp" target="_blank">
              here
            </Link>{" "}
            to contact candidate support for further information.
          </MessageBanner>
        ),
      },
      {
        titleText: "Drug Test",
        titleStatusPath: DRUG_TEST_STATUS_ENDPOINT,
        details: <div></div>,
        errorStatusDetailMessage: (
          <MessageBanner type={MessageBannerType.Warning}>
            Your Drug Test with Amazon came back with an error so we need to
            meet with you again. Please click{" "}
            <Link href="http://amazon.com/applicationhelp" target="_blank">
              here
            </Link>{" "}
            to contact candidate support to be scheduled for a Drug Test
            appointment
          </MessageBanner>
        ),
      },
    ],
    details: (
      <Text>
        At the new hire appointment, Amazon will initiate your background check,
        conduct pre-employment drug test and review work authorization (I-9)
        documents. Visit complete list of{" "}
        <Link href={USCIS_URL} target="_blank">
          acceptable I-9 documents
        </Link>
        .
      </Text>
    ),
    analyticsOnExpand: "pre-employment screening",
  },
  {
    titleText: "Provide personal information",
    details: function CompletePICTask(status: StatusEnum) {
      return (
        <PICTask status={status} href={PERSONAL_INFORMATION_COLLECTION_URL} />
      );
    },
    taskStatusName: "PIC",
    hideWhenError: true,
    hideOnTaskStatusError: true,
    hideInProd: true,
    analyticsOnExpand: "ash expand personal information",
  },
  {
    titleText: "Complete employment paperwork",
    details:
      "You’ll receive an email to complete Payroll Direct Deposit, Terms of Employment, Non-Disclosure Agreement and other employment related forms. " +
      "Complete and sign these forms before starting work at Amazon.",
    analyticsOnExpand: "new hire docs",
  },
  {
    titleText: "Complete new hire orientation",
    details:
      "Before you start with Amazon, you will receive an email with training instructions. The training will include general orientation as well as job and location specific information.",
    analyticsOnExpand: "orientation",
  },
];

export interface TaskGroup {
  name: string;
  tasks: Array<Task>;
}

export const taskGroups: Array<TaskGroup> = [
  {
    name: "Before your pre-hire appointment",
    tasks: [tasks[0], tasks[2], tasks[4], tasks[1], tasks[3]],
  },
  {
    name: "During your appointment",
    tasks: [tasks[5], tasks[6]],
  },
  {
    name: "After your appointment",
    tasks: [tasks[7], tasks[8], tasks[9]],
  },
];

export const taskGroupsForDGS: Array<TaskGroup> = [
  {
    name: "Before your pre-hire appointment",
    tasks: [tasks[2], tasks[4], tasks[1]],
  },
  {
    name: "During your appointment",
    tasks: [tasks[6]],
  },
  {
    name: "After your appointment",
    tasks: [tasks[7], tasks[8], tasks[9]],
  },
];
